import {Component, inject} from "@angular/core";
import {Store} from "@ngrx/store";
import {CommonModule, DatePipe} from "@angular/common";
import {MatCardModule} from "@angular/material/card";
import {MatTableModule} from "@angular/material/table";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatTooltipModule} from "@angular/material/tooltip";
import {FormsModule} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {
    selectComparableCompanies,
    selectComparableCompaniesData,
    selectComparableCompaniesEdit,
    selectComparableCompaniesLastRefreshed,
    selectComparableCompaniesLoading,
    selectComparableCompaniesLowConfidence,
    selectIsAnyAssetFormInEditMode,
    selectSelectedAssetId
} from "../../store/asset/asset.selectors";
import {ComparableCompanyActions} from "../../store/asset.actions";
import {ComparableCompany, ComparableCompanySet} from "../../generated-sources/api";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {CardContextMenuDirective} from "../../../shared/comments/card-context-menu/card-context-menu.directive";
import {CardCommentsButtonComponent} from "../../../shared/comments/card-comments-button/card-comments-button.component";

@Component({
    selector: "valumize-asset-comparable-companies",
    templateUrl: "./asset-comparable-companies.component.html",
    styleUrls: ["./asset-comparable-companies.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatTableModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        DecimalFormatPipe,
        DatePipe,
        CardContextMenuDirective,
        CardCommentsButtonComponent
    ]
})
export class AssetComparableCompaniesComponent {
    private readonly store = inject(Store);

    comparableCompanies$ = this.store.select(selectComparableCompanies);
    comparableCompaniesData$ = this.store.select(selectComparableCompaniesData);
    isEditable$ = this.store.select(selectComparableCompaniesEdit);
    isLoading$ = this.store.select(selectComparableCompaniesLoading);
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    assetId$ = this.store.select(selectSelectedAssetId);
    lastRefreshed$ = this.store.select(selectComparableCompaniesLastRefreshed);
    isLowConfidence$ = this.store.select(selectComparableCompaniesLowConfidence);

    newTicker = "";
    newCompanyName = "";

    displayedColumns: string[] = [
        "companyName", "ticker", "currencyIso", "industry", "revenue", "revenueGrowth",
        "ebitdaMargin", "enterpriseValue", "evEbitda", "marketCap", "exchange", "analystNote"
    ];
    displayedColumnsWithActions = [...this.displayedColumns, "actions"];

    edit(): void {
        this.store.dispatch(ComparableCompanyActions.edit());
    }

    save(assetId: number, companies: ComparableCompany[]): void {
        this.store.dispatch(ComparableCompanyActions.save({assetId, companies}));
    }

    cancel(): void {
        this.store.dispatch(ComparableCompanyActions.cancel());
    }

    removeComp(assetId: number, compId: number): void {
        this.store.dispatch(ComparableCompanyActions.remove({assetId, compId}));
    }

    updateNote(assetId: number, compId: number, note: string): void {
        this.store.dispatch(ComparableCompanyActions.updateNote({assetId, compId, note}));
    }

    updateNoteFromEvent(assetId: number, compId: number, event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.updateNote(assetId, compId, value);
    }

    addComp(assetId: number, existingCompanies: ComparableCompany[]): void {
        const ticker = this.newTicker.trim().toUpperCase();
        const companyName = this.newCompanyName.trim() || ticker;
        if (!ticker) return;

        const newComp: ComparableCompany = {ticker, companyName, source: "MANUAL"};
        const updated = [...existingCompanies, newComp];
        this.store.dispatch(ComparableCompanyActions.save({assetId, companies: updated}));
        this.newTicker = "";
        this.newCompanyName = "";
    }


    dispatchSuggest(): void {
        this.store.dispatch(ComparableCompanyActions.suggest());
    }

    scrollToEnrichmentFields(): void {
        document.getElementById("general-information")?.scrollIntoView({behavior: "auto"});
    }

    dispatchRefresh(data: ComparableCompanySet | null): void {
        const tickers = data?.companies?.map(c => c.ticker).filter(t => !!t) ?? [];
        const existingCompanies = data?.companies ?? [];
        this.store.dispatch(ComparableCompanyActions.refresh({tickers, existingCompanies}));
    }

}
